// bd-deepcontext-b4zw — fetch-based SSE stream of a job's runs, lifted out of
// ScheduledJobs.handleShowRuns (same approach the URL Analyzer uses). Line
// framing lives in ../../hooks/sseParser so it can be tested without a network.
import type { ScheduledJobRun } from "../jobRuns/types";
import { createSseParser } from "../../hooks/sseParser";

export interface RunsStreamHandle {
  close: () => void;
  /** Mirrors EventSource's field so callers can keep the same liveness check. */
  readyState?: number;
}

/**
 * Open the runs stream for `jobId` and push every `runs_update` to `onRuns`.
 * Returns a handle whose `close()` aborts the request. Aborts itself once the
 * backend reports `all_complete`.
 */
export const streamJobRuns = (
  jobId: string,
  onRuns: (runs: ScheduledJobRun[]) => void,
): RunsStreamHandle => {
  const controller = new AbortController();

  const streamUpdates = async () => {
    try {
      // Use the API base URL for SSE
      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const response = await fetch(
        `${baseURL}/api/scheduled-jobs/${jobId}/runs/stream`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
            Accept: "text/event-stream",
          },
          credentials: "include",
          signal: controller.signal,
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      if (!response.body) {
        throw new Error("Response body is null");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parser = createSseParser();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        for (const { event, data } of parser.push(
          decoder.decode(value, { stream: true }),
        )) {
          if (event === "runs_update") {
            onRuns(data.runs);
          } else if (event === "all_complete") {
            console.log("All job runs completed");
            controller.abort();
          }
        }
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("SSE streaming error:", error);
      }
    }
  };

  streamUpdates();

  return { close: () => controller.abort() };
};
